import type { ReactNode } from "react";
import { AlertTriangle, Info } from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { cn } from "../../../lib/utils";
import { DASH, formatClock, formatRate, humanize, isNum, runState } from "../format";
import type { FeatureOutcome, Turn } from "../types";

/** Turn index -> "Turn 4 · 0:31", using the same response's turns. */
export type TurnRef = (index: number | null | undefined) => string;

export function makeTurnRef(turns: Turn[]): TurnRef {
  const byIndex = new Map(turns.map((turn) => [turn.index, turn]));
  return (index) => {
    if (!isNum(index)) return "";
    const turn = byIndex.get(index);
    const at = turn && isNum(turn.start) ? ` · ${formatClock(turn.start)}` : "";
    return `Turn ${index + 1}${at}`;
  };
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="rounded-xl border border-line bg-canvas px-3 py-2.5">
      <div className="text-[11px] font-medium uppercase tracking-wide text-ink-3">{label}</div>
      <div className="mt-0.5 text-lg font-semibold text-ink">{value}</div>
      {hint ? <div className="text-xs text-ink-3">{hint}</div> : null}
    </div>
  );
}

export function Block({ title, children, className }: { title: string; children: ReactNode; className?: string }) {
  return (
    <section className={cn("space-y-2", className)}>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-3">{title}</h4>
      {children}
    </section>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="text-sm text-ink-3">{children}</p>;
}

/** Something the feature could not measure, and why. */
export function Unavailable({ children }: { children: ReactNode }) {
  return (
    <p className="flex items-start gap-2 rounded-lg bg-soft px-3 py-2 text-xs text-ink-2">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden />
      <span>{children}</span>
    </p>
  );
}

export function Quote({ text, turn }: { text: string | null | undefined; turn?: string }) {
  if (!text && !turn) return null;
  return (
    <p className="text-xs text-ink-2">
      {text ? <q className="italic">{text}</q> : null}
      {turn ? <span className="ml-1.5 text-ink-3">{turn}</span> : null}
    </p>
  );
}

/** Label/value distribution (0..1 shares) as labelled bars. */
export function Distribution({ values, label }: { values: Record<string, number> | null | undefined; label: string }) {
  const entries = Object.entries(values ?? {}).filter(([, v]) => isNum(v)).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <Empty>{DASH}</Empty>;
  return (
    <ul className="space-y-1.5" aria-label={label}>
      {entries.map(([name, share]) => (
        <li key={name} className="flex items-center gap-2 text-xs">
          <span className="w-24 shrink-0 text-ink-2">{humanize(name)}</span>
          <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-soft" aria-hidden>
            <span className="block h-full rounded-full bg-brand" style={{ width: `${Math.min(100, Math.max(0, share * 100))}%` }} />
          </span>
          <span className="w-10 text-right tabular-nums text-ink-2">{formatRate(share)}</span>
        </li>
      ))}
    </ul>
  );
}

/** Skipped and failed say why instead of rendering an empty panel. */
export function FeatureFrame<R>({
  title,
  outcome,
  children,
}: {
  title: string;
  outcome: FeatureOutcome<R> | undefined;
  children: (result: R) => ReactNode;
}) {
  const state = runState(outcome);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-ink">{title}</h3>
        <Badge tone={state.tone}>{humanize(state.kind)}</Badge>
      </div>
      {state.kind === "completed" && outcome?.result ? (
        children(outcome.result)
      ) : (
        <p className="flex items-start gap-2 rounded-lg border border-line bg-canvas px-3 py-3 text-sm text-ink-2">
          {state.kind === "failed" ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden /> : <Info className="mt-0.5 h-4 w-4 shrink-0 text-ink-3" aria-hidden />}
          <span>{state.kind === "completed" ? "Completed, but no result was stored." : state.text}</span>
        </p>
      )}
    </div>
  );
}

export const yesNo = (value: boolean | null | undefined) => (value === true ? "Yes" : value === false ? "No" : DASH);
